import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function Navbar({ closeMobileMenu }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      if (closeMobileMenu) closeMobileMenu();
      setIsMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    if (closeMobileMenu) closeMobileMenu();
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="nav-bar">
      <style>{`
        /* --- MOBILE FIRST BASE STYLING (Slim Layout) --- */
        .nav-bar {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 4px 12px; /* Fixed: Reduced from 8px to 4px to make it significantly slimmer */
          box-sizing: border-box;
          gap: 10px;
          background-color: #004b87;
          position: relative; 
          font-family: Arial, sans-serif;
        }

        .hamburger-btn {
          display: block;
          background: none;
          border: none;
          font-size: 1.6rem; /* Fixed: Reduced from 2rem to match the slim line */
          color: #ffffff;
          cursor: pointer;
          padding: 2px 4px;  /* Fixed: Tightened vertical button spacing */
          line-height: 1;
          order: 1;
        }

        .navbar-search-container {
          display: flex;
          align-items: center;
          flex-grow: 1;
          max-width: 75%;
          order: 2;
        }

        .navbar-search-container form {
          width: 100%;
          display: flex;
          align-items: center;
          position: relative;
        }

        /* Mobile Menu Drawer Content Container */
        .main-nav-links {
          display: ${isMobileMenuOpen ? 'flex' : 'none'} !important;
          flex-direction: column !important;
          width: 100% !important;
          background-color: #003360 !important; 
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 9999;
          padding: 16px !important;
          gap: 12px !important;
          margin: 0;
          list-style: none;
          box-sizing: border-box;
        }

        /* Top level main menu entries on Mobile */
        .main-nav-links > li > a {
          color: #ffffff !important; 
          font-weight: 600;
          font-size: 1.05rem;
          text-decoration: none;
          display: block;
          padding: 8px 4px;
        }

        /* Sub-menus / Dropdowns on Mobile view */
        .mega-menu {
          display: block !important;
          position: static !important;
          box-shadow: none !important;
          padding: 4px 0 !important;
          margin-top: 6px;
          margin-bottom: 8px;
          border-radius: 4px;
          box-sizing: border-box;
          width: 100%;
        }

        /* Force high contrast white text for all sub-links inside the blue blocks on Mobile */
        .mega-menu a {
          color: #ffffff !important; 
          font-size: 0.95rem !important;
          font-weight: 500 !important;
          padding: 12px 14px !important;
          text-decoration: none;
          display: block !important;
          visibility: visible !important;
          background-color: #00569d !important; 
          border-bottom: 1px solid #004b87 !important;
          box-sizing: border-box;
          width: 100%;
        }
        
        .mega-menu a:last-child {
          border-bottom: none !important;
        }

        .wide-menu-split, .standard-dropdown {
          display: flex !important;
          flex-direction: column !important;
          gap: 2px !important;
        }

        /* --- DESKTOP LAYERS (992px and up) --- */
        @media (min-width: 992px) {
          .nav-bar {
            padding: 0 24px;
            justify-content: space-between;
          }

          .hamburger-btn {
            display: none;
          }

          .navbar-search-container {
            order: 2;
            max-width: 260px;
            justify-content: flex-end;
          }

          /* Reset structural container to inline horizontal format row for desktop */
          .main-nav-links {
            display: flex !important;
            flex-direction: row !important;
            width: auto !important;
            background-color: transparent !important;
            box-shadow: none;
            position: static;
            padding: 0 !important;
            gap: 24px !important;
            order: 1;
          }

          /* Primary Navigation Header links */
          .main-nav-links > li > a {
            color: #ffffff !important;
            padding: 18px 0;
            display: block;
          }

          .dropdown-item {
            position: relative;
            display: flex;
            align-items: center;
            height: 100%;
          }

          /* Floating layout dropdown panels on Desktop view */
          .mega-menu {
            position: absolute !important;
            top: 100%;
            left: 0;
            background-color: #ffffff !important; 
            box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
            display: none !important;
            padding: 12px !important;
            border-left: none;
            border-top: 3px solid #004b87;
            min-width: 240px;
            border-radius: 0 0 4px 4px;
            z-index: 9999;
            margin-top: 0;
          }

          .mega-menu::before {
            content: '';
            position: absolute;
            top: -15px;
            left: 0;
            width: 100%;
            height: 15px;
            background: transparent;
          }

          /* Enforce charcoal gray readability on desktop white dropdown menus */
          .mega-menu a {
            color: #001e36 !important;
            background-color: transparent !important;
            padding: 10px 14px !important;
            font-size: 0.9rem !important;
            font-weight: 500 !important;
            transition: all 0.2s ease;
            border-radius: 4px;
            border-bottom: none !important;
            display: block !important;
            text-decoration: none;
          }

          .mega-menu a:hover {
            color: #ffffff !important;
            background-color: #004b87 !important;
          }

          .dropdown-item:hover .mega-menu {
            display: block !important;
          }
          
          .dropdown-item:hover .wide-menu-split {
            display: flex !important;
            flex-direction: row !important;
            min-width: 480px;
            gap: 12px !important;
          }
          
          .mega-col {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
        }
      `}</style>

      {/* HAMBURGER TOGGLE BUTTON */}
      <button className="hamburger-btn" onClick={toggleMobileMenu} aria-label="Toggle navigation">
        {isMobileMenuOpen ? '✕' : '≡'}
      </button>

      {/* SEARCH INPUT */}
      <div className="navbar-search-container">
        <form onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search specialties, visas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '6px 32px 6px 12px', /* Fixed: Slightly tightened default height space */
              borderRadius: '20px',
              border: 'none',
              fontSize: '0.85rem',         /* Fixed: Slightly smaller text elements */
              width: '100%',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <button type="submit" style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#666', padding: '2px' }}>
            🔍
          </button>
        </form>
      </div>

      {/* NAVIGATION LINKS */}
      <ul className="main-nav-links" onClick={handleLinkClick}>
        <li className="dropdown-item">
          <Link to="/treatments" style={{ textDecoration: 'none' }}>Specialties & Treatments ▾</Link>
          <div className="mega-menu wide-menu-split">
            <div className="mega-col">
              <Link to="/cardiology">Cardiology (Heart Bypass)</Link>
              <Link to="/orthopedics">Orthopedics (Knee Joint)</Link>
              <Link to="/oncology">Oncology (Cancer Care)</Link>
            </div>
            <div className="mega-col">
              <Link to="/dental">Dental Packages</Link>
              <Link to="/destinations">Top Medical Cities</Link>
              <Link to="/success-stories">Success Stories</Link>
            </div>
          </div>
        </li>

        <li className="dropdown-item">
          <Link to="/patient-support" style={{ textDecoration: 'none' }}>Patient Services ▾</Link>
          <div className="mega-menu standard-dropdown">
            <Link to="/compare-costs">Free Cost Calculator</Link>
            <Link to="/medical-opinion">Get Medical Opinion</Link>
            <Link to="/doctor-consultation">Doctor Consultations</Link>
            <Link to="/doctor-network">Vetted Doctor Network</Link>
            <Link to="/follow-up-care">Follow-Up & Recovery</Link>
          </div>
        </li>

        <li className="dropdown-item">
          <Link to="/visa-guides" style={{ textDecoration: 'none' }}>Medical Visa Help ▾</Link>
          <div className="mega-menu standard-dropdown">
            <Link to="/visa-assistance">Visa Assistance Guide</Link>
            <Link to="/visa-processing">Online Application Timeline</Link>
            <Link to="/visa-extension">Extension & Rules 2026</Link>
            <Link to="/travel-advisories">Travel & Flight Advisory</Link>
          </div>
        </li>

        <li><Link to="/hospitals" style={{ textDecoration: 'none' }}>Find Hospitals</Link></li>
        <li><Link to="/medical-updates" style={{ textDecoration: 'none' }}>Medical Updates</Link></li>
      </ul>
    </nav>
  );
}

export default Navbar;